import { useState } from 'react';
import { QRCode } from 'antd';
import CodeInput, { emptyCode } from '../../../reusable/CodeInput/CodeInput';
import Button from '../../../reusable/Button/Button';
import CopyButton from '../../../reusable/CopyButton/CopyButton';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import { authErrorMessage } from '../../../store/api/authHelpers';
import {
  useDisable2faMutation,
  useEnable2faMutation,
  useGet2faStatusQuery,
  useSetup2faMutation,
} from '../../../store/api/twoFactorApi';
import { t } from '../../../i18n/i18n';
import './Security.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** Arming 2FA is two screens deep, and turning it off is a third. */
const STATUS = 'status';
const SETUP = 'setup';
const VERIFY = 'verify';
const DISABLE = 'disable';

const CODE_LENGTH = 6;
const EMPTY_CODE = emptyCode(CODE_LENGTH);

/** What the trader is about to do, shown before they commit to doing it. */
const HOW_IT_WORKS = [
  {
    id: 'scan',
    icon: 'profile-platform',
    title: 'Scan the QR code',
    desc: 'Add Alpha Capital to your authenticator app.',
  },
  {
    id: 'code',
    icon: 'profile-password',
    title: 'Enter the code',
    desc: 'Confirm the 6 digits the app generates.',
  },
  {
    id: 'login',
    icon: 'profile-2fa',
    title: 'Sign in with it',
    desc: 'A fresh code is asked for at every sign-in.',
  },
];

const TIPS = [
  'Keep the authenticator app to yourself — a code is as good as your password.',
  'Use an app you already trust: Google Authenticator, Authy, 1Password.',
  'Take the setup key down now if you want a second device enrolled later.',
  'Only turn 2FA off if you have lost the device it is enrolled on.',
];

/** The API returns the secret unspaced; groups of four are what you can read off a screen. */
const spaced = (secret) => (secret ?? '').replace(/(.{4})/g, '$1 ').trim();

/** Numbered rail above the two setup screens — where you are, and what's left. */
function Stepper({ stage }) {
  const steps = [
    { id: SETUP, label: t('Scan') },
    { id: VERIFY, label: t('Verify') },
  ];

  return (
    <ol className="tfa-stepper">
      {steps.map((step, i) => {
        const current = stage === step.id;
        const done = stage === VERIFY && step.id === SETUP;
        return (
          <li
            key={step.id}
            className={`tfa-stepper__step${current ? ' is-current' : ''}${done ? ' is-done' : ''}`}
          >
            <span className="tfa-stepper__mark">
              {done ? <span className="icon tfa-stepper__tick" style={icon('check-sm')} /> : i + 1}
            </span>
            <span className="tfa-stepper__label">{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function ProfileSecurity() {
  const { data: enabled, isLoading, isError, refetch } = useGet2faStatusQuery();
  const [setup2fa, setupState] = useSetup2faMutation();
  const [enable2fa, enableState] = useEnable2faMutation();
  const [disable2fa, disableState] = useDisable2faMutation();

  const [stage, setStage] = useState(STATUS);
  const [digits, setDigits] = useState(EMPTY_CODE);
  const [error, setError] = useState(null);

  /**
   * What `POST /setup/` handed back. Held here rather than in the cache because
   * `temp_secret` is a one-shot handle: it is only good until `enable` accepts
   * a code against it, and a second setup call mints a different one.
   */
  const [pending, setPending] = useState(null);

  const code = digits.join('');
  const busy = setupState.isLoading || enableState.isLoading || disableState.isLoading;

  const reset = () => {
    setStage(STATUS);
    setDigits(EMPTY_CODE);
    setError(null);
  };

  const startSetup = async () => {
    setError(null);
    try {
      const data = await setup2fa().unwrap();
      setPending(data);
      setDigits(EMPTY_CODE);
      setStage(SETUP);
    } catch (err) {
      setError(authErrorMessage(err));
    }
  };

  /**
   * Sends the code — from the button, or on its own the moment the sixth digit
   * lands (typed or pasted), as the sign-in dialog does: no point spending the
   * code's 30 seconds on a click. `busy` keeps a second send out while one is
   * in flight.
   */
  const submitCode = async (otpCode = code) => {
    if (busy) return;
    if (otpCode.length < CODE_LENGTH) {
      setError('Enter all 6 digits from your authenticator app');
      return;
    }
    setError(null);
    try {
      if (stage === DISABLE) {
        await disable2fa({ otpCode }).unwrap();
      } else {
        await enable2fa({ otpCode, tempSecret: pending?.temp_secret }).unwrap();
        setPending(null);
      }
      reset();
    } catch (err) {
      setError(authErrorMessage(err));
      setDigits(EMPTY_CODE);
    }
  };

  // The hero's whole treatment keys off one of four states, so resolve it once.
  const state = isLoading ? 'loading' : isError ? 'error' : enabled ? 'on' : 'off';

  const HERO = {
    loading: {
      chip: <StatusChip tone="neutral">{t('Checking')}</StatusChip>,
      line: t('Checking whether two-factor authentication is on for this account.'),
    },
    error: {
      chip: <StatusChip tone="danger">{t('Unavailable')}</StatusChip>,
      line: t('We could not reach the security service. Try again in a moment.'),
    },
    on: {
      chip: <StatusChip tone="success">{t('Enabled')}</StatusChip>,
      line: t('You will be asked for a code from your authenticator app each time you sign in.'),
    },
    off: {
      chip: <StatusChip tone="neutral">{t('Disabled')}</StatusChip>,
      line: t('Your account is protected by your password alone.'),
    },
  }[state];

  return (
    <section className="profile-section profile-security">
      <div className="profile-head">
        <h2 className="profile-head__title">{t('Two-Factor Authentication')}</h2>
        <p className="profile-head__desc">
          {t('A second check at sign-in, on top of your password. With 2FA on, a stolen password is not enough to reach your accounts or request a payout.')}
        </p>
      </div>

      {/* The hero is the one thing every stage keeps — it answers "is my account
          protected right now", and stepping through setup shouldn't take that
          off the screen. */}
      <div className={`tfa-hero tfa-hero--${state}`}>
        <span className="tfa-hero__badge">
          <span className="icon tfa-hero__glyph" style={icon('profile-2fa')} />
        </span>

        <div className="tfa-hero__text">
          <div className="tfa-hero__title">
            {t('Authenticator app')}
            {HERO.chip}
          </div>
          <p className="tfa-hero__desc">{HERO.line}</p>
        </div>

        {state === 'error' && (
          <Button variant="ghost" onClick={refetch}>
            {t('Retry')}
          </Button>
        )}
        {state === 'on' && stage === STATUS && (
          <Button variant="ghost" onClick={() => setStage(DISABLE)}>
            {t('Turn off')}
          </Button>
        )}
        {state === 'off' && stage === STATUS && (
          <Button variant="submit" loading={setupState.isLoading} onClick={startSetup}>
            {t('Enable 2FA')}
          </Button>
        )}
      </div>

      {/* A setup call that never got as far as its own screen has nowhere else
          to report itself. */}
      {stage === STATUS && error && <p className="tfa-hero__error">{t(error)}</p>}

      {state === 'off' && stage === STATUS && (
        <>
          <div className="tfa-explain">
            <h3 className="tfa-explain__title">{t('How it works')}</h3>
            <div className="tfa-explain__steps">
              {HOW_IT_WORKS.map((step, i) => (
                <article key={step.id} className="tfa-card">
                  <div className="tfa-card__head">
                    <span className="tfa-card__medallion">
                      <span className="icon tfa-card__icon" style={icon(step.icon)} />
                    </span>
                    <span className="tfa-card__index">{`0${i + 1}`}</span>
                  </div>
                  <p className="tfa-card__title">{t(step.title)}</p>
                  <p className="tfa-card__desc">{t(step.desc)}</p>
                </article>
              ))}
            </div>
          </div>

          <ul className="tfa-tips">
            {TIPS.map((tip) => (
              <li key={tip} className="tfa-tips__item">
                <span className="tfa-tips__mark">
                  <span className="icon tfa-tips__tick" style={icon('check-sm')} />
                </span>
                {t(tip)}
              </li>
            ))}
          </ul>
        </>
      )}

      {(stage === SETUP || stage === VERIFY) && (
        <div className="tfa-panel">
          <Stepper stage={stage} />

          {stage === SETUP ? (
            <>
              <div className="tfa-panel__head">
                <h3 className="tfa-panel__title">{t('Scan the QR code')}</h3>
                <p className="tfa-panel__desc">
                  {t('Open Google Authenticator, Authy or any TOTP app and scan this to add Alpha Capital.')}
                </p>
              </div>

              {/* The tile stays white at both themes on purpose: a scanner needs
                  the light quiet zone around the modules. The value is the
                  backend's own `totp_uri` — building a second one here would be
                  a second place for the issuer to drift. */}
              <div className="tfa-qr">
                <QRCode
                  value={pending?.totp_uri ?? ''}
                  size={156}
                  bordered={false}
                  color="#090909"
                  bgColor="#ffffff"
                />
              </div>

              <div className="tfa-manual">
                <span className="tfa-manual__label">{t("Can't scan it? Enter this key")}</span>
                <div className="tfa-manual__row">
                  <span className="tfa-manual__key">{spaced(pending?.secret)}</span>
                  <CopyButton value={pending?.secret ?? ''} label={t('Setup key')} />
                </div>
              </div>

              <div className="tfa-panel__actions">
                <Button variant="ghost" onClick={reset}>
                  {t('Back')}
                </Button>
                <Button variant="submit" onClick={() => setStage(VERIFY)}>
                  {t('Continue to verification')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="tfa-panel__head">
                <h3 className="tfa-panel__title">{t('Enter the 6-digit code')}</h3>
                <p className="tfa-panel__desc">
                  {t('Type the code your authenticator app is showing for Alpha Capital. It changes every 30 seconds.')}
                </p>
              </div>

              <CodeInput
                digits={digits}
                invalid={Boolean(error)}
                disabled={busy}
                // Ready to type on arrival, and back in the first box after a
                // wrong code clears them.
                autoFocus
                onChange={(next) => {
                  setDigits(next);
                  setError(null);
                  // The sixth digit is the whole answer — send it straight away.
                  if (next.join('').length === CODE_LENGTH) submitCode(next.join(''));
                }}
              />

              {error ? (
                <p className="tfa-panel__error">{t(error)}</p>
              ) : (
                <p className="tfa-panel__hint">
                  {t('Lost the QR code?')}{' '}
                  <button type="button" className="tfa-panel__link" onClick={() => setStage(SETUP)}>
                    {t('Go back and scan again')}
                  </button>
                </p>
              )}

              <div className="tfa-panel__actions">
                <Button variant="ghost" disabled={busy} onClick={() => setStage(SETUP)}>
                  {t('Back')}
                </Button>
                <Button variant="submit" loading={enableState.isLoading} onClick={() => submitCode()}>
                  {t('Enable 2FA')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Turning it off asks for a live code too: the point of 2FA is that a
          stolen password on its own can't undo it. */}
      {stage === DISABLE && (
        <div className="tfa-panel tfa-panel--danger">
          <div className="tfa-panel__head">
            <h3 className="tfa-panel__title">{t('Turn off two-factor authentication')}</h3>
            <p className="tfa-panel__desc">
              {t('Confirm with a code from your authenticator app. Afterwards your password is the only thing standing between someone and this account.')}
            </p>
          </div>

          <CodeInput
            digits={digits}
            invalid={Boolean(error)}
            disabled={busy}
            autoFocus
            onChange={(next) => {
              setDigits(next);
              setError(null);
              // The sixth digit is the whole answer — send it straight away.
              if (next.join('').length === CODE_LENGTH) submitCode(next.join(''));
            }}
          />

          {error && <p className="tfa-panel__error">{t(error)}</p>}

          <div className="tfa-panel__actions">
            <Button variant="ghost" disabled={busy} onClick={reset}>
              {t('Cancel')}
            </Button>
            <Button variant="submit" loading={disableState.isLoading} onClick={() => submitCode()}>
              {t('Turn off 2FA')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
